import { ChangeEvent, FC, useRef } from "react";

import { serverImportExternalCourse } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { TextField } from "mds/components/TextField";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentOrg } from "store/selectors";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

type AdminSettingsMainProps = {
  courses: FullSectionType[];
  readOnly?: boolean;
};

export const AdminSettingsMain: FC<AdminSettingsMainProps> = ({ courses, readOnly = false }) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const courseInput = useRef<HTMLInputElement>(null);

  if (!courses || !currentOrg) {
    return <LoadingScreen>{t("home.settings.no_courses")}</LoadingScreen>;
  }

  const handleNameChange = (name: string) => {
    storeApi.orgs.partial_update({ id: currentOrg.id, name });
  };

  const onImportCourse = async (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const file = event.target.files![0];

    const formData = new FormData();
    formData.append("data", file);

    const response = await serverImportExternalCourse(formData);

    if (response) {
      toastSuccessMessage(t("home.settings.import_course_success"));
      trackEvent("Course imported", {
        ...GENERAL_EVENT_CATEGORY,
        courseCode: response.course_code.title,
        courseTerm: response.term.title,
        courseTitle: response.title,
      });
    }
  };

  return (
    <div>
      <label>
        <span className="h6 text-black-tint-40">{t("home.settings.institution_name")}</span>
        <TextField
          disabled={readOnly}
          value={currentOrg?.name}
          wrapperClassName="flex justify-start items-center"
          isEditing
          onChange={(event) => handleNameChange(event.target.value)}
        />
      </label>

      <div className="flex items-center">
        <Button
          className="mt-2"
          disabled={readOnly}
          eventName="Org Settings - Import course"
          kind="white"
          size="s"
          onClick={() => courseInput.current?.click()}
        >
          {t("home.settings.import_course")}
        </Button>

        <input
          className="invisible w-0"
          disabled={readOnly}
          ref={courseInput}
          type="file"
          onChange={(event) => {
            onImportCourse(event).catch(toastLocalizedOperationCatcher("import_course_failure"));
          }}
        />
      </div>
    </div>
  );
};
